import React, { useState, useMemo, useEffect } from "react";
import { Button, Select } from "antd";
import { useGetSubjectsQuery } from "../../../../hooks/api/Admin/Courses/useGetSubjectsQuery";
import { useGetTutorQuery } from "../../../../hooks/api/Tutors/Auth/useGetTutorQuery";
import { useUpdateSubjectExpertise } from "../../../../hooks/api/Tutors/UpdateProfile/useUpdateSubjectExpertise";
import { set } from "lodash";

const { Option, OptGroup } = Select;

interface Subject {
  _id: string;
  subject: string;
  addedBy?: string;
  createdAt: string;
  updatedAt: string;
}

interface AreaOfExpertise {
  id: string;
  subject: string;
  category: string;
  createdAt: string;
  updatedAt: string;
}

type SubjectsByLevel = Record<string, Subject[]>;

const UpdateExpertiseSelect: React.FC = () => {
  const [previousSelection, setPreviousSelection] = useState<{ ids: string[] }>(
    { ids: [] }
  );
  const [newSelection, setNewSelection] = useState<string[]>([]);

  const { data: tutorData } = useGetTutorQuery();

  const { data: subjectsResponse, isLoading } = useGetSubjectsQuery({
    page: 1,
    limit: 100,
  });

  const categories: SubjectsByLevel = subjectsResponse?.data?.categories || {};

  const { mutateAsync } = useUpdateSubjectExpertise();

  // 🔥 Collect all valid subject IDs
  const allSubjectIds = useMemo(() => {
    return new Set(
      Object.values(categories)
        .flat()
        .map((subject) => subject._id)
    );
  }, [categories]);

  // 🔥 Sync tutor expertise ONLY when subjects exist
  useEffect(() => {
    if (tutorData?.data?.areaOfExpertise?.length && allSubjectIds.size) {
      const ids = tutorData.data.areaOfExpertise
        .map((item: AreaOfExpertise) => item.id)
        .filter((id: string) => allSubjectIds.has(id));

      setPreviousSelection({ ids });
    }
  }, [tutorData, allSubjectIds]);

  const categoryOptions = useMemo(() => {
    return Object.entries(categories).map(([categoryName, subjects]) => (
      <OptGroup key={categoryName} label={categoryName}>
        {subjects.map((subject) => (
          <Option
            key={subject._id}
            value={subject._id}
            disabled={previousSelection.ids.includes(subject._id)}
          >
            {subject.subject}
          </Option>
        ))}
      </OptGroup>
    ));
  }, [categories, previousSelection]);

  return (
    <div>
      <Select
        mode="multiple"
        maxCount={8}
        placeholder="Select up to 8 subjects"
        className="w-full mt-2 capitalize"
        size="large"
        value={newSelection}
        onChange={(ids: string[]) => setNewSelection(ids)}
        loading={isLoading}
        optionFilterProp="children"
      >
        {categoryOptions}
      </Select>

      <p className="mt-3 text-gray500">Select maximum of 10</p>

      <Button
        type="primary"
        className="mt-8 w-full"
        size="large"
        loading={isLoading}
        onClick={() =>
          mutateAsync(newSelection, {
            onSuccess: () => {
              setNewSelection([]);
            },
          })
        }
      >
        Continue
      </Button>
    </div>
  );
};

export default UpdateExpertiseSelect;
